import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Button,
	DatePicker,
	Form,
	FormInstance,
	Grid,
	Input,
	Select,
	Collapse,
} from 'antd';
import { UserRole } from '../shared/VolunteerApplicationServiceTypes';
import { PositionAssignmentFunctionaryInfo } from '../shared/MySrcm';
import { useTranslation } from 'react-i18next';
import {
	API_DATE_FORMAT,
	DATE_FORMAT_DD_MM_YYYY,
	ADD_FUNCTIONARY_UI_CONFIG,
	FunctionaryType,
	Gender,
	VALID_ABHYASI_ID_REGEX,
	VALID_EMAIL_REGEX,
	VALID_STRING_INPUT_REGEX,
} from '../shared/Constants';
import { useAuth } from '../hooks/useAuth';
import { OptionData } from '../hooks/useZonesDropdown';
import LeftArrowWhite from '../assets/images/arrow-left-white.svg';
import Icon from './ui-components/Icon';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useAddOrEditFunctionaryPage } from '../pages/functionary/AddOrEditFunctionaryPage/useAddOrEditFunctionaryPage';
import HomeRegion from '../pages/functionary/FunctionariesList/HomeRegion';
import HomeZone from '../pages/functionary/FunctionariesList/HomeZone';
import AuthorizedRegions from '../pages/functionary/FunctionariesList/RegionsAuthorisedForDropdown';
import AuthorizedZones from '../pages/functionary/FunctionariesList/ZonesAuthorizedForDropdown';
import useUserSession from '../hooks/useUserSession';
import dayjs, { normalizeFunctionaryType } from '../shared/Utils';
import { useToastMessage } from '../hooks/useToastMessage';
import { useConfirmPopup } from '../hooks/useConfirmPopup';
import type { Dayjs } from 'dayjs';
import { getPositionAssignmentUsers } from '../service/MySrcmService';
import { useLoadingIndicator } from '../hooks/useLoadingIndicator';

interface OptionRegionData {
	value: string | undefined;
	label: string | undefined;
}

interface OptionZoneData {
	value: string;
}

interface OptionAuthorizedZoneData {
	value: string | undefined;
	label: string | undefined;
	regionId?: number;
}

interface FunctionaryDetailsFormProps {
	form: FormInstance;
	functionary?: PositionAssignmentFunctionaryInfo;
	isEditForm?: boolean;
	onAddEditFunctionarySubmit?: (values: FunctionaryFormValues) => void;
	onCancelClick?: () => void;
	submitButtonDisabled?: boolean;
	functionarySrcmIdParam?: string;
}

type FunctionaryMapping = {
	type?: string;
	roleId?: number;
	authorizedRegions?: Array<{ value: string | number; label?: string }>;
	authorizedZones?: Array<{ value: string | number; label?: string }>;
	startDate?: string;
	endDate?: string;
};

type FunctionaryFormValues = PositionAssignmentFunctionaryInfo & {
	status?: string;
	functionaryMappings?: FunctionaryMapping[];
};

const { useBreakpoint } = Grid;
const { Panel } = Collapse;

export const FunctionaryDetailsForm: React.FC<FunctionaryDetailsFormProps> = ({
	form,
	functionary,
	isEditForm,
	onAddEditFunctionarySubmit,
	onCancelClick,
	submitButtonDisabled,
	functionarySrcmIdParam,
}: FunctionaryDetailsFormProps) => {
	const { t, i18n } = useTranslation();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { showToastMessage } = useToastMessage();
	const { confirmPopUpAction } = useConfirmPopup();
	const { setLoading } = useLoadingIndicator();

	const { fetchPreceptorByAbhyasiIdFunctionary } =
		useAddOrEditFunctionaryPage();

	const [homeRegionData, setHomeRegionData] = useState<OptionRegionData>();
	const [homeZone, setHomeZone] = useState<OptionZoneData>();
	const [authorizedRegion, setAuthorizedRegion] =
		useState<OptionRegionData[]>();
	const [statusValue, setStatusValue] = useState<boolean | undefined>();
	const [isPreceptorLoading, setIsPreceptorLoading] = useState<boolean>(false);
	const [isFetchedDataBySRCMID, setIsDataFetchedBySRCMID] =
		useState<boolean>(true);
	const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
	const [disableSRCMIDSearchClick, setDisableSRCMIDSearchClick] =
		useState(false);
	const [isSRCMIDVerified, setIsSRCMIDVerified] = useState(true);
	const screens = useBreakpoint();
	const lengthyLanguages = ['ta', 'te', 'ka', 'ma'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [functionaryPanels, setFunctionaryPanels] = useState<string[]>([]);
	const [functionarySectionError, setFunctionarySectionError] = useState<
		string | null
	>(null);
	const [formChange, setFormChange] = useState(false);
	const functionarySectionRef = useRef<HTMLDivElement | null>(null);
	const addFunctionaryPanelRef = useRef<(() => void) | null>(null);
	const functionaryMappings =
		(Form.useWatch('functionaryMappings', form) as FunctionaryMapping[]) || [];

	const showFunctionaryType = selectedRole === UserRole.SNP_ADMIN;
	const disableSrcmIdField = functionarySrcmIdParam !== '-1';
	const disableFields = isFetchedDataBySRCMID || isEditForm;
	const shouldHideHomeMappings = ADD_FUNCTIONARY_UI_CONFIG.hideHomeMappings;
	const startDateRef = useRef<Record<string, boolean>>({});
	const endDateRef = useRef<Record<string, boolean>>({});

	const functionaryTypeOptions: OptionData[] = [
		{ label: `${t('proposer')}`, value: FunctionaryType.PROPOSER },
		{ label: `${t('approver')}`, value: FunctionaryType.APPROVER },
		{ label: `${t('careTeam')}`, value: FunctionaryType.CARE_TEAM },
		{
			label: `${t('preworkAdmin')}`,
			value: FunctionaryType.PREWORK_ADMIN,
		},
		{
			label: `${t('npsAdmin')}`,
			value: FunctionaryType.NPS_ADMIN,
		},
		{
			label: `${t('screeningAdmin')}`,
			value: FunctionaryType.SCREENING_ADMIN,
		},
		{
			label: `${t('preworkMentor')}`,
			value: FunctionaryType.PREWORK_MENTOR,
		},
		{
			label: `${t('snpAdmin')}`,
			value: FunctionaryType.SNP_ADMIN,
		},
		{
			label: `${t('examAdmin')}`,
			value: FunctionaryType.EXAM_ADMIN,
		},
	];

	const allFunctionaryTypesUsed =
		new Set(
			functionaryMappings.map((m) => m?.type?.toLowerCase()).filter(Boolean)
		).size >= functionaryTypeOptions.length;

	const disabledAddFunctionaryTypeButton = !isEditForm
		? isSRCMIDVerified
		: false;

	const handleSearchButtonClick = useCallback(async () => {
		const mandatoryFieldsToCheckAfterSearch = {
			printName: t('name'),
			email: t('email'),
			mobile: t('phoneNo'),
			gender: t('gender'),
		};
		if (!form.getFieldValue('srcmId') || !fetchPreceptorByAbhyasiIdFunctionary)
			return;

		setIsPreceptorLoading(true);

		try {
			const preceptorBySearch = await fetchPreceptorByAbhyasiIdFunctionary(
				String(form.getFieldValue('srcmId'))
			);

			if (preceptorBySearch) {
				const preceptor = preceptorBySearch;
				setIsSRCMIDVerified(false);

				const mappedValues = {
					printName: preceptor.name,
					email: preceptor.email,
					mobile: preceptor.mobile,
					gender:
						preceptor.gender === 'M'
							? Gender.MALE
							: preceptor.gender === 'F'
							? Gender.FEMALE
							: '',
				};
				form.setFieldsValue(mappedValues);

				const missingFields = Object.entries(
					mandatoryFieldsToCheckAfterSearch
				).filter(([field]) => {
					const value = mappedValues[field as keyof typeof mappedValues];

					return value === undefined || value === null || value === '';
				});

				const hasMissingMandatoryFields = missingFields.length > 0;
				if (hasMissingMandatoryFields) {
					const fieldLabels = missingFields.map(([, label]) => label);

					const message =
						fieldLabels.length === 1
							? t('fieldIsMissing', {
									fields: fieldLabels[0],
							  })
							: t('fieldsAreMissing', {
									fields: fieldLabels.join(', '),
							  });

					showToastMessage('error', message);
				}

				setDisableSRCMIDSearchClick(true);
				setIsDataFetchedBySRCMID(true);
			} else {
				form.resetFields();
				setIsDataFetchedBySRCMID(true);
			}
		} finally {
			setIsPreceptorLoading(false);
		}
	}, [fetchPreceptorByAbhyasiIdFunctionary, form, showToastMessage, t]);

	const handleClearButtonClick = useCallback(() => {
		form.resetFields();
		setHomeRegionData({
			label: undefined,
			value: undefined,
		});
		setAuthorizedRegion([]);
		setIsSRCMIDVerified(true);
		setDisableSRCMIDSearchClick(false);
		setIsDataFetchedBySRCMID(true);
		setFormChange(false);
	}, [form]);

	const handleRegionsChange = useCallback(
		(value: OptionRegionData) => {
			form.setFieldsValue({
				homeZoneToDisplay: { label: undefined, value: undefined },
			});
			setHomeZone(undefined);
			setHomeRegionData(value);
		},
		[form]
	);

	const handleHomeZoneChange = useCallback((value: OptionZoneData) => {
		setHomeZone(value);
	}, []);

	const handleSubmit = async () => {
		try {
			if (!isEditForm) {
				setLoading(true);
				await form.validateFields();
				const values = form.getFieldsValue(true) as FunctionaryFormValues;
				const payload = {
					...values,
					isActive: true,
				};

				const ref = values.srcmId;

				const response = await getPositionAssignmentUsers({
					srcmId: ref,
					page: 1,
					page_size: 100,
				});

				const filteredUser = response?.results?.find(
					(user) => user.ref === ref
				);

				// Existing role types from API
				const existingRoleTypes =
					filteredUser?.assignments?.map(
						(assignment) => assignment.position.code
					) ?? [];

				// Role types selected in form
				const selectedRoleTypes =
					values.functionaryMappings?.map(
						(roleAssignment) => roleAssignment.type
					) ?? [];

				// Duplicate inside form itself
				const hasDuplicateInForm =
					selectedRoleTypes.length !== new Set(selectedRoleTypes).size;

				if (hasDuplicateInForm) {
					showToastMessage('error', 'Duplicate role types are not allowed');

					return;
				}

				// Compare with API roles
				const duplicateRole = selectedRoleTypes.find((role) =>
					existingRoleTypes.includes(role ?? '')
				);
				const existingAssignments = filteredUser?.assignments ?? [];

				const duplicateActiveRole = selectedRoleTypes.find((role) =>
					existingAssignments.some(
						(assignment) =>
							assignment.position.code === role &&
							assignment.assign_active === true
					)
				);
				if (duplicateActiveRole) {
					showToastMessage('error', `${duplicateRole ?? ''} already exists`);

					return;
				}

				onAddEditFunctionarySubmit?.(payload);
			} else {
				await form.validateFields();
				const values = form.getFieldsValue(true) as FunctionaryFormValues;
				const payload = {
					...values,
					isActive: values.status === 'Active',
				};
				onAddEditFunctionarySubmit?.(payload);
			}
		} catch (error) {
			showToastMessage('error', t('pleaseFillRequiredFields'));
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!isEditForm) {
			form.setFieldsValue({
				homeRegionToDisplay: {
					label: homeRegionData?.label,
					value: homeRegionData?.label,
				},
			});
		}
	}, [form, homeRegionData, isEditForm]);

	useEffect(() => {
		if (isEditForm && functionary && !isInitialLoadDone) {
			const { homeRegion, homeRegionId, regions } = functionary;

			setHomeRegionData({ label: homeRegion, value: String(homeRegionId) });

			const fetchedAuthorizedRegions =
				regions?.map((region) => ({
					label: region.label,
					value: String(region.value),
				})) || [];

			setAuthorizedRegion(fetchedAuthorizedRegions);

			setIsDataFetchedBySRCMID(false);
		}
	}, [isEditForm, functionary, isInitialLoadDone]);

	useEffect(() => {
		if (!isInitialLoadDone && authorizedRegion && authorizedRegion.length > 0) {
			setIsInitialLoadDone(true);
		}
	}, [authorizedRegion, isInitialLoadDone]);

	const handleStatusChange = useCallback((value: boolean) => {
		setFormChange(true);
		setStatusValue(value);
	}, []);

	return (
		<Form
			name="basic"
			layout="vertical"
			size="middle"
			labelCol={{ span: 16 }}
			initialValues={{ remember: true }}
			autoComplete="off"
			form={form}
		>
			<div className="relative flex items-center justify-center border-b-[2px] border-gray-700 pb-6 mt-4">
				{/* Back Button - aligned to the left */}
				<div className="hidden md:block">
					<Button
						type="primary"
						onClick={onCancelClick}
						className="absolute left-0 top-0 font-bold text-sm flex items-center gap-2"
					>
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</Button>
				</div>

				{/* Title - centered */}
				<div className="text-center font-sans font-bold text-lg">
					{functionarySrcmIdParam !== '-1'
						? showFunctionaryType
							? t('editFunctinary')
							: t('editProposer')
						: showFunctionaryType
						? t('addFunctionary')
						: t('addProposer')}
				</div>
			</div>
			<div className="flex sm:justify-left md:justify-center md:items-left">
				<div className="bg-transparent md:bg-white sm:p-0 md:p-8 sm:rounded-none md:rounded-lg sm:border-none md:border md:border-solid md:border-gray-200 md:w-full md:max-w-4xl">
					<div className="flex flex-wrap overflow-y-auto pb-[80px] md:pb-[10px]">
						<div
							className={`w-full md:w-1/2 px-2 ${
								!isEditForm ? 'mb-0' : 'mb-4'
							}`}
						>
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('SRCMID')}
										<span className="text-red"> *</span>
									</span>
								}
								name="srcmId"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_ABHYASI_ID_REGEX,
										message: <span>{t('invalidSRCMID')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={20}
									disabled={disableSrcmIdField || disableSRCMIDSearchClick}
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						{!isEditForm && (
							<div
								className={`flex gap-4 w-full md:w-1/2 px-2 ${
									isMobile ? 'mt-0' : 'mt-8'
								} mb-4`}
							>
								<Button
									onClick={() => {
										void handleSearchButtonClick();
									}}
									className={`h-9 text-primary-blue font-sans font-bold ${
										isStackVertical ? 'p-2 mt-3' : ''
									} ${isMobile ? 'w-full' : 'flex-1'}`}
									icon={<SearchOutlined />}
									disabled={
										!form.getFieldValue('srcmId') ||
										form.getFieldError('srcmId').length > 0 ||
										disableSRCMIDSearchClick
									}
									loading={isPreceptorLoading}
								>
									{t('search')}
								</Button>
								<Button
									onClick={() => {
										void handleClearButtonClick();
									}}
									className={`h-9 text-primary-blue font-sans font-bold ${
										isStackVertical ? 'p-2 mt-3' : ''
									} ${isMobile ? 'w-full' : 'flex-1'}`}
									disabled={!disableSRCMIDSearchClick}
									loading={isPreceptorLoading}
								>
									{t('clear')}
								</Button>
							</div>
						)}
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('name')}
										<span className="text-red"> *</span>
									</span>
								}
								name="printName"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_STRING_INPUT_REGEX,
										message: <span>{t('onlyAllowedSpecialCharacters')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={100}
									className="flex min-w-[200px]"
									size="large"
									disabled={disableFields}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650  font-sans">
										{t('email')}
										<span className="text-red"> *</span>
									</span>
								}
								name="email"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_EMAIL_REGEX,
										message: <span>{t('invalidEmail')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									className="flex min-w-[200px]"
									size="large"
									disabled={disableFields}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650  font-sans">
										{t('phoneNo')}
										<span className="text-red"> *</span>
									</span>
								}
								name="mobile"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									className="flex min-w-[200px]"
									size="large"
									disabled={disableFields}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('gender')}
										<span className="text-red"> *</span>
									</span>
								}
								name="gender"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Select
									className="flex min-w-[200px]"
									options={[
										{ value: 'MALE', label: `${t('male')}` },
										{ value: 'FEMALE', label: `${t('female')}` },
									]}
									size="large"
									disabled={disableFields}
								/>
							</Form.Item>
						</div>
						{!shouldHideHomeMappings && (
							<>
								<div className="w-full md:w-1/2 px-2 mb-4">
									<Form.Item
										label={
											<span className="text-gray-650 font-sans">
												{t('homeRegion')}
											</span>
										}
										name="homeRegionToDisplay"
										className="mb-[16px]"
									>
										<HomeRegion
											functionarySrcmId={srcmId}
											value={homeRegionData}
											onChange={handleRegionsChange}
											isFetchedDataBySRCMID={disableFields}
										/>
									</Form.Item>
								</div>
								<div className="w-full md:w-1/2 px-2 mb-4">
									<Form.Item
										label={
											<span className="text-gray-650 font-sans">
												{t('homeZone')}
											</span>
										}
										name="homeZoneToDisplay"
										className="mb-[16px]"
									>
										<HomeZone
											functionarySrcmId={srcmId}
											value={homeZone}
											onChange={handleHomeZoneChange}
											regionId={homeRegionData?.value}
											key={homeRegionData?.value}
											isFetchedDataBySRCMID={disableFields}
										/>
									</Form.Item>
								</div>
							</>
						)}

						{isEditForm && (
							<div className="w-full md:w-1/2 px-2 mb-4">
								<Form.Item
									label={<span className="text-gray-650">{t('status')}</span>}
									name="isActive"
									className="mb-[16px] font-sans"
								>
									<Select
										onChange={handleStatusChange}
										value={statusValue}
										options={[
											{ value: true, label: `${t('active')}` },
											{ value: false, label: `${t('inactive')}` },
										]}
										className="flex min-w-[200px]"
										size="large"
									/>
								</Form.Item>
							</div>
						)}
					</div>

					<div className="w-full px-2 mb-2" ref={functionarySectionRef}>
						<div className="flex items-center justify-between mb-2">
							<div className="text-gray-650 font-sans font-semibold">
								{t('functionaryTypes') || 'Functionary Types'}
							</div>
							<Button
								type="primary"
								onClick={() => {
									addFunctionaryPanelRef.current?.();
									setFunctionarySectionError(null);
									setFormChange(true);
								}}
								className="h-9 font-sans font-semibold"
								disabled={
									disabledAddFunctionaryTypeButton || allFunctionaryTypesUsed
								}
							>
								{t('addFunctionaryType')}
							</Button>
						</div>
						<Form.List name="functionaryMappings">
							{(fields, { add, remove }) => {
								addFunctionaryPanelRef.current = () => {
									const selectedValues = functionaryMappings
										.map((m) => m?.type?.toLowerCase())
										.filter(Boolean);

									const firstAvailable = functionaryTypeOptions.find(
										(opt) => !selectedValues.includes(opt.value.toLowerCase())
									);

									const newKey = fields.length.toString();

									add({
										type: firstAvailable?.value,
										regions: [],
										zones: [],
									});

									setFunctionaryPanels((prev) =>
										Array.from(new Set([...prev, newKey]))
									);
								};

								if (!fields.length) {
									return (
										functionarySectionError && (
											<div className="text-red text-sm mb-2">
												{functionarySectionError}
											</div>
										)
									);
								}

								return (
									<>
										{functionarySectionError && (
											<div className="text-red text-sm mb-2">
												{functionarySectionError}
											</div>
										)}
										<Form.Item shouldUpdate noStyle>
											{({ getFieldValue }) => (
												<Collapse
													activeKey={functionaryPanels}
													onChange={(keys) =>
														setFunctionaryPanels(
															Array.isArray(keys) ? keys : [String(keys)]
														)
													}
												>
													{fields.map((field, index) => {
														const startDateValue = form.getFieldValue([
															'functionaryMappings',
															field.name,
															'startDate',
														]) as string;

														const endDateValue = form.getFieldValue([
															'functionaryMappings',
															field.name,
															'endDate',
														]) as string;

														if (!(field.key in startDateRef.current)) {
															startDateRef.current[field.key] =
																!!startDateValue;
														}
														if (!(field.key in endDateRef.current)) {
															endDateRef.current[field.key] = !!endDateValue;
														}

														const isStartDateLocked =
															startDateRef.current[field.key];
														const isEndDateLocked =
															endDateRef.current[field.key];

														const currentMapping =
															functionaryMappings[index] || {};

														const functionaryTypeValue = getFieldValue([
															'functionaryMappings',
															field.name,
															'type',
														]) as string | undefined;

														const shouldHideAuthorizedFields = [
															FunctionaryType.CARE_TEAM,
															FunctionaryType.PREWORK_MENTOR,
															FunctionaryType.SNP_ADMIN,
														].includes(functionaryTypeValue || '');

														const updatedFunctionaryTypeOptions =
															functionaryTypeOptions.map((opt) => {
																const isAlreadySelected =
																	functionaryMappings?.some(
																		(m, i) =>
																			i !== index &&
																			m?.type?.toLowerCase() ===
																				opt.value?.toLowerCase()
																	);

																return {
																	...opt,
																	disabled: isAlreadySelected,
																};
															});

														const selectedRegions =
															(currentMapping?.authorizedRegions as OptionRegionData[]) ||
															[];

														const selectedRegionIds = selectedRegions
															.map((region) => Number(region.value))
															.filter((id): id is number => !Number.isNaN(id));

														const headerLabel =
															normalizeFunctionaryType(functionaryTypeValue) ||
															`${t('functionary')} ${index + 1}`;

														return (
															<Panel
																header={headerLabel}
																key={field.key}
																extra={
																	<Button
																		type="link"
																		danger
																		aria-label={t('delete') || 'Delete'}
																		title={t('delete') || 'Delete'}
																		onClick={() => {
																			confirmPopUpAction(
																				'confirmDelete',
																				'areYouSureYouWantToDelete',

																				() => {
																					remove(field.name);
																					delete startDateRef.current[
																						field.key
																					];
																					delete endDateRef.current[field.key];

																					setFunctionaryPanels((prev) =>
																						prev.filter(
																							(panelKey) =>
																								panelKey !==
																								field.name.toString()
																						)
																					);

																					setFormChange(true);
																				},
																				'delete'
																			);
																		}}
																		icon={<DeleteOutlined />}
																	/>
																}
															>
																<div className="flex flex-wrap -mx-2">
																	<div className="w-full px-2 mb-4">
																		<Form.Item
																			label={t('functionaryType')}
																			name={[field.name, 'type']}
																			rules={[
																				{
																					required: true,
																					message:
																						t('functionaryTypeRequired') ||
																						'Functionary Type is required',
																				},
																			]}
																		>
																			<Select
																				placeholder={t('functionaryType')}
																				options={updatedFunctionaryTypeOptions}
																				size="large"
																				className="flex min-w-[200px]"
																				onChange={() => {
																					setFormChange(true);
																					setFunctionaryPanels((prev) =>
																						Array.from(
																							new Set([
																								...prev,
																								field.name.toString(),
																							])
																						)
																					);
																				}}
																			/>
																		</Form.Item>
																	</div>
																	<>
																		{!shouldHideAuthorizedFields && (
																			<>
																				<div className="w-full md:w-1/2 px-2 mb-4">
																					<Form.Item
																						label={t('regionsAuthorized')}
																						name={[
																							field.name,
																							'authorizedRegions',
																						]}
																						rules={[
																							{
																								required:
																									!shouldHideAuthorizedFields,
																								type: 'array',
																								min: 1,
																								message:
																									t('regionsRequired') ||
																									'At least one region is required',
																							},
																						]}
																						valuePropName="value"
																						getValueFromEvent={(
																							value: OptionRegionData[]
																						) => value}
																					>
																						<AuthorizedRegions
																							functionarySrcmId={srcmId}
																							onChange={(
																								value: OptionRegionData[]
																							) => {
																								setFormChange(true);
																								form.setFieldValue(
																									[
																										'functionaryMappings',
																										field.name,
																										'authorizedRegions',
																									],
																									value
																								);
																							}}
																							isFetchedDataBySRCMID={false}
																							isFunctionaryCareTeam={false}
																						/>
																					</Form.Item>
																				</div>
																				<div className="w-full md:w-1/2 px-2 mb-4">
																					<Form.Item
																						label={t('zonesAuthorized')}
																						name={[
																							field.name,
																							'authorizedZones',
																						]}
																						rules={[
																							{
																								required:
																									!shouldHideAuthorizedFields,
																								type: 'array',
																								min: 1,
																								message:
																									t('zonesRequired') ||
																									'At least one zone is required',
																							},
																						]}
																						valuePropName="value"
																						getValueFromEvent={(
																							value: OptionAuthorizedZoneData[]
																						) => value}
																					>
																						<AuthorizedZones
																							onChange={(value) => {
																								form.setFieldValue(
																									[
																										'functionaryMappings',
																										field.name,
																										'authorizedZones',
																									],
																									value
																								);
																								setFormChange(true);
																							}}
																							authorizedRegionIds={
																								selectedRegionIds
																							}
																							isFunctionaryCareTeam={false}
																							authorizedRegion={
																								(currentMapping?.authorizedRegions as OptionRegionData[]) ||
																								[]
																							}
																						/>
																					</Form.Item>
																				</div>
																			</>
																		)}
																		<div className="w-full md:w-1/2 px-2 mb-4">
																			<Form.Item
																				label={t('startDate')}
																				name={[field.name, 'startDate']}
																				getValueProps={(value?: string) => ({
																					value: value
																						? dayjs(value, [
																								API_DATE_FORMAT,
																								DATE_FORMAT_DD_MM_YYYY,
																						  ])
																						: undefined,
																				})}
																				getValueFromEvent={(
																					date: Dayjs | null
																				) =>
																					date
																						? date.format(
																								DATE_FORMAT_DD_MM_YYYY
																						  )
																						: undefined
																				}
																			>
																				<DatePicker
																					className="flex w-full min-w-[200px]"
																					size="large"
																					format={DATE_FORMAT_DD_MM_YYYY}
																					disabled={isStartDateLocked}
																					disabledDate={(current) =>
																						current &&
																						current < dayjs().startOf('day')
																					}
																					onChange={() => {
																						setFormChange(true);
																						form.setFieldValue(
																							[
																								'functionaryMappings',
																								field.name,
																								'endDate',
																							],
																							null
																						);
																					}}
																				/>
																			</Form.Item>
																		</div>
																		<div className="w-full md:w-1/2 px-2 mb-4">
																			<Form.Item
																				label={t('endDate')}
																				name={[field.name, 'endDate']}
																				getValueProps={(value?: string) => ({
																					value: value
																						? dayjs(value, [
																								API_DATE_FORMAT,
																								DATE_FORMAT_DD_MM_YYYY,
																						  ])
																						: undefined,
																				})}
																				getValueFromEvent={(
																					date: Dayjs | null
																				) =>
																					date
																						? date.format(
																								DATE_FORMAT_DD_MM_YYYY
																						  )
																						: undefined
																				}
																			>
																				<DatePicker
																					className="flex w-full min-w-[200px]"
																					size="large"
																					disabled={isEndDateLocked}
																					disabledDate={(current) => {
																						const startDate =
																							form.getFieldValue([
																								'functionaryMappings',
																								field.name,
																								'startDate',
																							]) as string | undefined;

																						if (!startDate || !current)
																							return false;

																						const minEndDate = dayjs(
																							startDate,
																							DATE_FORMAT_DD_MM_YYYY
																						).add(1, 'day');

																						return current.isBefore(
																							minEndDate,
																							'day'
																						);
																					}}
																					format={DATE_FORMAT_DD_MM_YYYY}
																					onChange={() => {
																						setFormChange(true);
																					}}
																				/>
																			</Form.Item>
																		</div>
																	</>
																</div>
															</Panel>
														);
													})}
												</Collapse>
											)}
										</Form.Item>
									</>
								);
							}}
						</Form.List>
					</div>
					<style>
						{`
								.mobile-top-shadow {
									box-shadow: 0 -4px 6px rgba(133, 133, 133, 0.12);
							}
							@media (min-width: 768px) {
								.mobile-top-shadow {
									box-shadow: none;
								}
							}
						`}
					</style>

					<div className="flex flex-row py-[24px] px-[16px] fixed md:relative bottom-0 left-0 right-0 md:p-[8px] justify-center md:justify-end bg-white md:bg-transparent mt-[16px] md:mt-[12px] mb-[8px] md:mb-[0px] mobile-top-shadow">
						<Button
							onClick={onCancelClick}
							className="w-[180px] h-[36px] md:w-[140px] border-solid border-primary-blue text-primary-blue"
							variant="outlined"
						>
							{t('cancel')}
						</Button>
						<Button
							onClick={() => {
								void handleSubmit();
							}}
							type="primary"
							disabled={submitButtonDisabled || !formChange}
							className="w-[180px] h-[36px] md:w-[140px] ml-[16px]"
						>
							{t('submit')}
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
};
